import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { queryKeys } from '../lib/queryKeys'

/**
 * Custom hook for managing clients (TanStack Query)
 * @param {{ enabled?: boolean }} [options] - Optional. Set enabled: false to avoid starting the query (e.g. to avoid cancel on Strict Mode double-mount).
 */
export const useClients = (options = {}) => {
  const { userProfile, apiTenantSlug } = useAuth()
  const { tenant } = useTenant()
  const queryClient = useQueryClient()
  const isTenantAdmin = userProfile?.role === 'admin' && !!userProfile?.tenant_id
  const hasTenantContext = !!(apiTenantSlug || tenant?.slug)
  const enabled = !!userProfile?.id && (options.enabled !== false) && (!isTenantAdmin || hasTenantContext)
  const tenantCacheKey = apiTenantSlug || tenant?.slug || ''

  const {
    data: clients = [],
    isLoading: loading,
    error,
    refetch: fetchClients,
  } = useQuery({
    queryKey: queryKeys.clients.list(tenantCacheKey),
    queryFn: ({ signal }) => api.clients.getAll({ signal }),
    enabled,
  })

  const invalidateClientRelated = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.stats.all })
  }

  const createMutation = useMutation({
    mutationFn: (clientData) => api.clients.create(clientData),
    onSuccess: invalidateClientRelated,
  })

  const updateMutation = useMutation({
    mutationFn: ({ clientId, updates }) => api.clients.update(clientId, updates),
    onSuccess: invalidateClientRelated,
  })

  const deleteMutation = useMutation({
    mutationFn: (clientId) => api.clients.delete(clientId),
    onSuccess: invalidateClientRelated,
  })

  const createClient = async (clientData) => {
    if (userProfile?.role !== 'admin') {
      return { data: null, error: new Error('Only admins can create clients') }
    }
    try {
      const result = await createMutation.mutateAsync(clientData)
      return { data: result?.client ?? result, warning: result?.warning, error: null }
    } catch (err) {
      console.error('Error creating client:', err)
      return { data: null, warning: null, error: err }
    }
  }

  const updateClient = async (clientId, updates) => {
    if (userProfile?.role !== 'admin') {
      return { data: null, warning: null, error: new Error('Only admins can update clients') }
    }
    try {
      const result = await updateMutation.mutateAsync({ clientId, updates })
      return { data: result?.client ?? result, warning: result?.warning ?? null, error: null }
    } catch (err) {
      console.error('Error updating client:', err)
      return { data: null, warning: null, error: err }
    }
  }

  const deleteClient = async (clientId) => {
    if (userProfile?.role !== 'admin') {
      return { error: new Error('Only admins can delete clients') }
    }
    try {
      await deleteMutation.mutateAsync(clientId)
      return { error: null }
    } catch (err) {
      console.error('Error deleting client:', err)
      return { error: err }
    }
  }

  return {
    clients,
    loading: loading || (isTenantAdmin && !hasTenantContext),
    error: error?.message ?? null,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
  }
}
